// Shared formatters for portal / admin. apps/pay keeps its own (public payer surface; see its format.ts), and sandbox
// keeps a copy for its self-contained SDK: keep those two in sync.

export function formatCurrency(amount: number | string | undefined, currency: string) {
  const num = typeof amount === "string" ? parseFloat(amount) : (amount ?? 0);
  const symbol = currency === "GBP" ? "£" : currency === "USD" ? "$" : currency === "EUR" ? "€" : currency;
  return `${symbol}${num.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatNumber(value: number | string | undefined | null): string {
  if (value == null) return "0";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("en-GB");
}

// en-GB ICU now writes September as "Sept"; one abbreviation everywhere, as the date pickers (date-fns) show.
const sep = (formatted: string) => formatted.replace(/\bSept\b/, "Sep");

export function formatDate(dateStr: string | undefined | null, month: "short" | "long" = "short") {
  if (!dateStr) return "N/A";
  return sep(
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month,
      year: "numeric",
    }),
  );
}

export function formatDateTime(dateStr: string | undefined | null, month: "short" | "long" = "short") {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  const date = sep(
    d.toLocaleDateString("en-GB", {
      day: "numeric",
      month,
      year: "numeric",
    }),
  );
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date}, ${time}`;
}

type UnitOfMeasure = "Gram" | "Kilogram" | "Millilitre" | "Litre" | null | undefined;

/** Short symbol for the structured weight/volume unit the backend parses for catch-weight matching. */
export function unitOfMeasureAbbr(uom: UnitOfMeasure): string | null {
  switch (uom) {
    case "Gram":
      return "g";
    case "Kilogram":
      return "kg";
    case "Millilitre":
      return "mL";
    case "Litre":
      return "L";
    default:
      return null;
  }
}

/** Unit label for a line: structured `unitOfMeasure` (what matching keys on), else free-text `unit`, else null. */
export function formatUnit(unit?: string | null, unitOfMeasure?: UnitOfMeasure): string | null {
  return unitOfMeasureAbbr(unitOfMeasure) ?? unit ?? null;
}
